/**
 * <rad-recommendations>: lazy product recommendations for the product page and the basket.
 *
 * One small module, no imports. The element holds a list (data-rad-rec-list) that starts as a skeleton of the
 * same height as the cards. When it comes within 600 px of the viewport it fetches
 *   {data-url}?section_id={data-section-id}&product_id={id}&limit={data-limit}&intent={data-intent}
 * once per id in data-product-ids (at most data-max-queries), takes the [data-rad-rec-item] nodes out of
 * each response, merges them round-robin, drops ids listed in data-exclude and duplicates, cuts the list at
 * data-limit and swaps the cards in. Responses are cached per URL for the page. When nothing comes back the
 * closest [data-rad-recs] is hidden. Add-to-basket inside a card is Horizon's product-form-component.
 *
 * Analytics: CustomEvents bubble from the element, no personal data, intent and source always present.
 *   rec_impression {intent, source, section_id, product_ids: string[], count}   once, when half the list is visible
 *   rec_click      {intent, source, section_id, product_id, position, action}   action is "view" (link) or "add" (form)
 */

const ROOT_MARGIN = '600px 0px';
const cache = new Map();

/**
 * Pull the recommendation items out of a Section Rendering API response.
 * @param {string} html
 * @param {Document} [doc] a Document to parse with (tests pass one; the browser uses DOMParser)
 * @returns {{id: string, node: Element}[]}
 */
export function parseItems(html, doc) {
  const parsed = doc || new DOMParser().parseFromString(html, 'text/html');
  const out = [];
  for (const node of parsed.querySelectorAll('[data-rad-rec-item]')) {
    const id = node.getAttribute('data-product-id') || '';
    if (id) out.push({ id, node });
  }
  return out;
}

/**
 * Merge several ranked lists round-robin, dropping excluded ids and duplicates, and cut at limit.
 * Pure: the order of `lists` is the order of the basket products, so each basket product contributes in turn.
 * @template T
 * @param {{id: string, node: T}[][]} lists
 * @param {Iterable<string>} exclude
 * @param {number} limit
 * @returns {{id: string, node: T}[]}
 */
export function mergeRecommendations(lists, exclude, limit) {
  const seen = new Set(exclude);
  const out = [];
  const longest = lists.reduce((m, l) => Math.max(m, l.length), 0);
  for (let i = 0; i < longest && out.length < limit; i += 1) {
    for (const list of lists) {
      if (out.length >= limit) break;
      const item = list[i];
      if (!item || seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

/**
 * Split a comma list attribute into trimmed, non-empty ids.
 * @param {string | undefined} value
 * @returns {string[]}
 */
export function splitIds(value) {
  return (value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Build the fetch URL for one product.
 * @param {{url: string, sectionId: string, intent: string, limit: number}} cfg
 * @param {string} productId
 */
export function buildUrl(cfg, productId) {
  const u = new URL(cfg.url, 'https://example.invalid');
  u.searchParams.set('section_id', cfg.sectionId);
  u.searchParams.set('product_id', productId);
  u.searchParams.set('limit', String(cfg.limit));
  u.searchParams.set('intent', cfg.intent);
  return u.pathname + u.search;
}

if (typeof customElements !== 'undefined' && !customElements.get('rad-recommendations')) {
  class RadRecommendations extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    #loadObserver = null;
    /** @type {IntersectionObserver | null} */
    #impressionObserver = null;
    /** @type {AbortController | null} */
    #abort = null;
    #impressionFired = false;
    /** @type {MutationObserver | null} */
    #hiddenObserver = null;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('submit', this.#onSubmit, true);
      if (this.dataset.loaded === 'true') {
        this.#watchImpression();
        return;
      }
      if (!this.querySelector('[data-rad-rec-skeleton]')) {
        this.#watchImpression();
        return;
      }
      this.#loadObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          this.#loadObserver = null;
          this.#load();
        },
        { rootMargin: ROOT_MARGIN }
      );
      this.#loadObserver.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('submit', this.#onSubmit, true);
      this.#loadObserver?.disconnect();
      this.#impressionObserver?.disconnect();
      this.#hiddenObserver?.disconnect();
      this.#hiddenObserver = null;
      this.#abort?.abort();
    }

    get #config() {
      const d = this.dataset;
      return {
        url: d.url || '/recommendations/products',
        sectionId: d.sectionId || '',
        intent: d.intent === 'complementary' ? 'complementary' : 'related',
        source: d.source || 'pdp',
        limit: Math.max(1, Math.min(10, parseInt(d.limit || '4', 10) || 4)),
        maxQueries: Math.max(1, parseInt(d.maxQueries || '1', 10) || 1),
        productIds: splitIds(d.productIds),
        exclude: splitIds(d.exclude),
        quickAdd: d.quickAdd !== 'false',
      };
    }

    async #load() {
      const cfg = this.#config;
      const list = this.querySelector('[data-rad-rec-list]');
      if (!list || !cfg.sectionId || cfg.productIds.length === 0) {
        this.#hide();
        return;
      }
      this.#abort = new AbortController();
      const ids = cfg.productIds.slice(0, cfg.maxQueries);
      let lists = [];
      try {
        lists = await Promise.all(ids.map((id) => this.#fetchItems(buildUrl(cfg, id), this.#abort.signal)));
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.#hide();
        return;
      }
      const merged = mergeRecommendations(lists, cfg.exclude, cfg.limit);
      if (merged.length === 0) {
        this.#hide();
        return;
      }
      const fragment = document.createDocumentFragment();
      merged.forEach((item, index) => {
        const node = document.importNode(item.node, true);
        node.setAttribute('data-position', String(index + 1));
        if (!cfg.quickAdd) node.querySelector('product-form-component')?.remove();
        fragment.appendChild(node);
      });
      list.replaceChildren(fragment);
      list.setAttribute('aria-busy', 'false');
      this.dataset.loaded = 'true';
      this.#watchImpression();
    }

    /**
     * @param {string} url
     * @param {AbortSignal} signal
     * @returns {Promise<{id: string, node: Element}[]>}
     */
    async #fetchItems(url, signal) {
      let html = cache.get(url);
      if (html === undefined) {
        const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
        if (!response.ok) return [];
        html = await response.text();
        cache.set(url, html);
      }
      return parseItems(html);
    }

    #hide() {
      const host = this.closest('[data-rad-recs]');
      if (host) host.hidden = true;
      this.dataset.loaded = 'empty';
      // 2026-10-30 (visual QC cart #4): a cart update morphs the section and drops `hidden` from the host, while the
      // list keeps its children (data-skip-subtree-update), so the skeleton came back for good. An empty marker in the
      // list survives the morph and CSS hides the host on it (snippets/rad-rec-styles.liquid).
      const list = this.querySelector('[data-rad-rec-list]');
      if (list) {
        const marker = document.createElement('li');
        marker.hidden = true;
        marker.setAttribute('data-rad-rec-empty', '');
        list.replaceChildren(marker);
        list.setAttribute('aria-busy', 'false');
      }
      // Browsers without :has(): put `hidden` back whenever a morph removes it while the marker is still there.
      if (host && typeof MutationObserver !== 'undefined' && !this.#hiddenObserver) {
        this.#hiddenObserver = new MutationObserver(() => {
          if (!host.hidden && this.querySelector('[data-rad-rec-empty]')) host.hidden = true;
        });
        this.#hiddenObserver.observe(host, { attributes: true, attributeFilter: ['hidden'] });
      }
    }

    #watchImpression() {
      if (this.#impressionFired || typeof IntersectionObserver === 'undefined') return;
      this.#impressionObserver?.disconnect();
      this.#impressionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          this.#impressionObserver = null;
          this.#fireImpression();
        },
        { threshold: 0.5 }
      );
      this.#impressionObserver.observe(this);
    }

    #fireImpression() {
      if (this.#impressionFired) return;
      this.#impressionFired = true;
      const cfg = this.#config;
      const ids = [...this.querySelectorAll('[data-rad-rec-item]')].map((n) => n.getAttribute('data-product-id') || '');
      this.dispatchEvent(
        new CustomEvent('rec_impression', {
          bubbles: true,
          detail: { intent: cfg.intent, source: cfg.source, section_id: cfg.sectionId, product_ids: ids, count: ids.length },
        })
      );
    }

    /**
     * @param {Element} target
     * @param {'view' | 'add'} action
     */
    #fireClick(target, action) {
      const item = target.closest('[data-rad-rec-item]');
      if (!item) return;
      const cfg = this.#config;
      this.dispatchEvent(
        new CustomEvent('rec_click', {
          bubbles: true,
          detail: {
            intent: cfg.intent,
            source: cfg.source,
            section_id: cfg.sectionId,
            product_id: item.getAttribute('data-product-id') || '',
            position: parseInt(item.getAttribute('data-position') || '0', 10) || 0,
            action,
          },
        })
      );
    }

    #onClick = (event) => {
      const link = event.target instanceof Element ? event.target.closest('[data-rad-rec-link]') : null;
      if (link) this.#fireClick(link, 'view');
    };

    #onSubmit = (event) => {
      if (event.target instanceof Element) this.#fireClick(event.target, 'add');
    };
  }

  customElements.define('rad-recommendations', RadRecommendations);
}
